"use client";

import { useEffect } from "react";
import { Icon } from "@ospweb/sdk-ui";
import { AppImage } from "@/src/components/NextImage";
import { cn } from "utils";
import { trackGiftModalView } from "@/src/hooks/onboarding/onboardingAnalytics";
import { useIsPc } from "@/src/hooks";
import { buildOpenReplayLabel } from "@/src/utils/openreplay";
import { gaTrackingAttrs } from "@/src/utils/tracking";
import { getGiftSetGoalPaywallCopy } from "./giftSetGoalPaywallCopy";
import { FREE_PLAN_OFFER_CARD_BG } from "./giftSetGoalPaywallAssets";
import type { GiftSetGoalPaywallModalContentProps } from "./types";

/**
 * Paywall – Free plan offer 卡片内容（Figma card `96097:351742`）。
 *
 * 壳层负责居中 + mask；此处只渲染圆角卡片，背景保持透明。
 */
export const GiftSetGoalPaywallModalContent: React.FC<
  GiftSetGoalPaywallModalContentProps
> = ({ isPc: isPcProp, onFinish }) => {
  const [isPcFromHook] = useIsPc();
  const isPc = isPcProp ?? isPcFromHook;
  const copy = getGiftSetGoalPaywallCopy();

  useEffect(() => {
    trackGiftModalView();
  }, []);

  return (
    <div
      className={cn(
        "relative flex w-full flex-col overflow-clip rounded-3 bg-background_solid_neutral_primary",
        isPc ? "max-w-[400px]" : "max-w-[345px]",
      )}
      data-testid="gift-set-goal-paywall-modal-view"
      data-figma-node-id="96097:351742"
      data-figma-card-node-id="96097:351742"
      {...gaTrackingAttrs("gift_modal_view")}
    >
      <div
        className="relative h-[146px] w-full shrink-0 overflow-clip"
        data-testid="gift-set-goal-paywall-card"
        data-figma-node-id="96097:351743"
        data-name="Goal Card V3"
      >
        <div
          className="pointer-events-none absolute inset-0 overflow-hidden"
          data-figma-node-id="96097:351744"
        >
          <AppImage
            src={FREE_PLAN_OFFER_CARD_BG}
            alt=""
            width={1035}
            height={438}
            loading="eager"
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(-89.76deg, rgba(44, 45, 10, 0.114) 14.13%, rgba(44, 45, 10, 0.64) 65.19%)",
            }}
          />
        </div>

        <div
          className="absolute left-6 top-[78px] flex flex-col items-start"
          data-figma-node-id="96097:351747"
        >
          <p
            className="body_medium_m text-content_aura_lv3_primary"
            data-figma-node-id="96097:351748"
          >
            {copy.beforeYouGo}
          </p>
          <p
            className="headline_regular_m tracking-[-0.96px] text-content_inverse_primary"
            data-figma-node-id="96097:351749"
          >
            {copy.takeYourPlanFree}
          </p>
        </div>
      </div>

      <div
        className="flex w-full flex-col items-center gap-6 p-6"
        data-figma-node-id="96097:351750"
      >
        <div
          className="flex w-full flex-col items-start gap-4"
          data-figma-node-id="96097:351751"
        >
          <p
            className="body_medium_l w-full text-content_primary"
            data-figma-node-id="96097:351752"
          >
            {copy.headline}
          </p>

          <div
            className="flex w-full flex-col items-start gap-2"
            data-figma-node-id="96097:351753"
          >
            {copy.features.map((text) => (
              <div
                key={text}
                className="flex w-full items-center gap-1"
                data-testid="gift-set-goal-paywall-feature"
              >
                <Icon
                  name="check"
                  size={16}
                  className="shrink-0 text-content_accent1_primary"
                />
                <span className="body_m min-w-0 flex-1 text-content_primary">
                  {text}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div
          className="flex w-full flex-col items-start gap-3"
          data-figma-node-id="96097:351763"
          data-testid="gift-set-goal-paywall-footer"
        >
          <button
            type="button"
            data-testid="gift-set-goal-paywall-unlock-cta"
            data-figma-node-id="96097:351764"
            className="button button_m emphasis_button flex h-11 w-full items-center justify-center rounded-2"
            {...gaTrackingAttrs("gift_setup_start")}
            data-openreplay-label={buildOpenReplayLabel(
              "Onboarding",
              "GiftSetGoalPaywall",
              "Unlock",
              copy.unlockCta,
            )}
            onClick={() => onFinish({ action: "unlock" })}
          >
            {copy.unlockCta}
          </button>
          <p
            className="body_medium_s w-full text-center text-content_quaternary"
            data-figma-node-id="96097:351765"
          >
            {copy.disclaimer}
          </p>
        </div>
      </div>
    </div>
  );
};
